import { Panel } from '@assets/component/Panel/Panel';
import { PanelHeader } from '@assets/component/Panel/PanelHeader/PanelHeader';
import {
    PanelColumn,
    PanelRow,
    PanelTable
} from '@assets/component/Panel/PanelTable/PanelTable';
import { GetSectionIconUrl } from '@assets/component/SectionIcons/SectionIcon';
import { Flex } from '@assets/component/UI/Flex/Flex';
import { useStore } from '@assets/model/Store';
import _, { max, min } from 'lodash';
import { observer } from 'mobx-react';
import { formatDollar, formatPercent } from '../../PanelElements/Formatters';
import { RowLabel } from '../../PanelElements/RowLable/RowLable';
import { RowValue } from '../../PanelElements/RowValue/RowValue';
import * as style from './PanelAcquisitions.module.less';
import { onEditReportField } from '../../UpdateFieldsUtil';
import { SchemaFieldKeys } from '@assets/model/FieldsSchema';
export const PanelAcquisitions = observer(() => {
    const store = useStore();
    const query = useStore().query;

    const getCompanyAcquisitionsTable = () => {
        const acquisitions = query.company_acquisitions;
        if (!acquisitions) {
            return null;
        }

        const rowDefaults: Partial<PanelRow> = {
            element: RowValue.value,
            label: RowLabel.label,
            formatter: data => formatDollar(data.toString())
        };

        const getYearRange = () => {
            const years = [];
            for (const customer of acquisitions) {
                for (const k in customer.ebitda) {
                    const e = customer.ebitda[k];
                    if (e.year > 0 && years.includes(e.year) === false) {
                        years.push(e.year);
                    }
                }
            }

            return { minYear: min(years), maxYear: max(years) };
        };
        const ALL_YEAR_CUSTOMER_INDEX = -1;
        const { minYear, maxYear } = getYearRange();
        const customerOrder = acquisitions
            .map((c, index) => ({ name: c.name, index }))
            .sort((a, b) => {
                const aEbitda = acquisitions[a.index]['ebitda'].find(
                    e => e.year === maxYear
                )?.ebitda;
                const bEbitda = acquisitions[b.index]['ebitda'].find(
                    e => e.year === maxYear
                )?.ebitda;
                if (aEbitda === undefined || bEbitda === undefined) {
                    return 0;
                }
                return bEbitda - aEbitda; // Sort by revenue in descending order
            })
            .map(c => c.index);

        customerOrder.push(ALL_YEAR_CUSTOMER_INDEX);

        const cols_companies_bought: PanelColumn[] = [];
        for (let i = minYear; i <= maxYear; i++) {
            cols_companies_bought.push({
                column: { source: i.toString(), display: i.toString() }
            });
        }

        cols_companies_bought.push({
            header: col => {
                return (
                    <Flex fillCell={true} justify="start">
                        {col.column.display}
                    </Flex>
                );
            },
            column: { source: null, display: 'Source' },
            element: RowValue.sources,
            dontUseRowFormatting: true,
            overrideValue: (field, column) => {
                return []; //Not done yet
            }
        });

        const getCustomerEbitda = (customerIndex: number, year: number) => {
            if (customerIndex === ALL_YEAR_CUSTOMER_INDEX) {
                return _(acquisitions)
                    .flatMap(c => c.ebitda)
                    .filter(e => e.year === year)
                    .reduce((acc, e) => acc + (e.ebitda || 0), 0);
            }
            const customer = acquisitions[customerIndex];
            if (!customer) {
                return 0;
            }

            return customer.ebitda.find(e => e.year === year)?.ebitda ?? 0;
        };

        const rows_customers: PanelRow[] = [];
        for (let i in customerOrder) {
            const customerIndex = customerOrder[i];
            const customerName =
                customerIndex == ALL_YEAR_CUSTOMER_INDEX
                    ? 'Totals'
                    : acquisitions[customerIndex].name;

            const row: PanelRow = {
                field: {
                    source: customerIndex.toString(),
                    display: customerName
                },
                value: (field, column) => {
                    const year = parseInt(column.source);
                    const customerIndex = parseInt(field.source);
                    var rev = getCustomerEbitda(customerIndex, year);
                    if (rev == 0) {
                        return '-';
                    }
                    return rev;
                },
                ...rowDefaults
            };
            rows_customers.push(row);
        }

        return (
            <PanelTable
                topLeftCellText="($ in Millions)"
                columns={cols_companies_bought}
                rows={rows_customers}
                disable_round_edges={true}
                onEditField={(row, column, newValue, rowIndex) => {
                    console.log('Edit field', row, column, newValue, rowIndex);
                }}
            />
        );
    };

    const getAcquisitionTablePerYear = () => {
        const rowDefaults: Partial<PanelRow> = {
            element: RowValue.value,
            label: RowLabel.label,
            formatter: data => formatDollar(data.toString()),
            editable: true
        };

        const rowPercentDefaults: Partial<PanelRow> = {
            element: RowValue.percent,
            label: RowLabel.label,
            formatter: data => formatPercent(data.toString()),
            editable: false
        };

        const getYearRange = () => {
            const reportYear = query.actualYear() || new Date().getFullYear();
            return { minYear: reportYear - 2, maxYear: reportYear + 1 };
        };
        const { minYear, maxYear } = getYearRange();

        const cols_acquisition_per_year: PanelColumn[] = [];
        for (let i = minYear; i <= maxYear; i++) {
            cols_acquisition_per_year.push({
                column: {
                    source: i.toString(),
                    display: `${i}`
                },
                element: RowValue.dollar
            });
        }

        cols_acquisition_per_year.push({
            header: col => {
                return (
                    <Flex fillCell={true} justify="start">
                        {col.column.display}
                    </Flex>
                );
            },
            column: { source: null, display: 'Source' },
            element: RowValue.sources,
            dontUseRowFormatting: true,
            overrideValue: (field, column) => {
                const groupSources = query.sources(field, column);
                if (groupSources.length === 0) {
                    return _.uniq(
                        query.top_customers.flatMap(c => c.page_sources)
                    );
                }

                return groupSources;
            }
        });

        const getAcquisitionEbitdaPerYear = (field, column) => {
            const year = parseInt(column.source);
            const val = query.valueByYear('acquisition_ebitda_per_year', year);

            if (val === 'N/A') {
                //If not available we try to calc based on entires in
                if (query.company_acquisitions) {
                    const totalEbitdaAcquisitions = query.company_acquisitions
                        .flatMap(c => c.ebitda)
                        .filter(e => e.year === year);

                    const finalTotalEbitda = totalEbitdaAcquisitions.reduce(
                        (acc, e) => acc + (e.ebitda || 0),
                        0
                    );
                    if (finalTotalEbitda === 0) {
                        return 'N/A';
                    }
                    return finalTotalEbitda.toFixed(2);
                }
            }

            return val;
        };

        const row_acquisition_per_year: PanelRow[] = [
            {
                field: {
                    source: 'acquisition_ebitda_per_year',
                    display: 'Total Acquisitions EBITDA'
                },
                value: getAcquisitionEbitdaPerYear,
                ...rowDefaults
            },
            {
                field: {
                    source: null,
                    display: 'Total Acquisitions EBITDA % of EBITDA'
                },
                value: (data, column) => {
                    const acqEbitda = getAcquisitionEbitdaPerYear(data, column);
                    if (acqEbitda === 'N/A') {
                        return 'N/A';
                    }

                    const year = parseInt(column.source);
                    const ebitdaPerYear = query.valueByYear(
                        'adjusted_ebitda_per_year',
                        year
                    );

                    if (ebitdaPerYear === 'N/A') {
                        return 'N/A';
                    }

                    const percent =
                        parseFloat(acqEbitda) / parseFloat(ebitdaPerYear);
                    return percent;
                },
                ...rowPercentDefaults
            }
        ];

        return (
            <PanelTable
                topLeftCellText=""
                columns={cols_acquisition_per_year}
                rows={row_acquisition_per_year}
                onEditField={onEditReportField}
            />
        );
    };
    const companyAcquisitions = getCompanyAcquisitionsTable();
    const acquisitionsTotal = getAcquisitionTablePerYear();

    const icon = GetSectionIconUrl('concentration');
    return (
        <>
            <Panel>
                <PanelHeader title="Acquisitions EBITDA" iconUrl={icon} />
                {store.showAdminConsole && companyAcquisitions}
                {acquisitionsTotal}
            </Panel>
        </>
    );
});
